/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Onboarding State Management
 *
 * Handles draft saving, resuming, and progress persistence for onboarding flow.
 */

import type { SubscriptionPlan } from "@/lib/constants/subscriptions";
import { logger } from "@/lib/utils/logger";

const ONBOARDING_DRAFT_KEY = "contractor-copilot-onboarding-draft";
const DRAFT_EXPIRY_DAYS = 7;

export interface OnboardingDraft {
  /** Clerk user id that saved this draft; used to avoid restoring another user's draft */
  userId?: string;
  selectedPlan: SubscriptionPlan;
  billingCycle: "monthly" | "annual";
  organizationName: string;
  industry: string;
  size: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  currentStep: string;
  savedAt: string;
  expiresAt: string;
}

/**
 * Save onboarding progress to localStorage (scoped to current user when userId is provided).
 */
export function saveOnboardingDraft(
  data: Partial<OnboardingDraft> & { userId?: string },
): void {
  try {
    const now = new Date();
    const expiresAt = new Date(
      now.getTime() + DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    );

    const draft: OnboardingDraft = {
      ...data,
      savedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    } as OnboardingDraft;

    localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(draft));
    logger.debug("[ONBOARDING] Draft saved", {
      step: data.currentStep,
      userId: data.userId ?? "(none)",
    });
  } catch (error) {
    logger.error("[ONBOARDING] Failed to save draft:", error);
  }
}

/**
 * Load onboarding draft from localStorage.
 * Returns null if no draft exists, draft is expired, or draft belongs to a different user.
 * Pass currentUserId so drafts from a previous user are ignored and cleared.
 */
export function loadOnboardingDraft(
  currentUserId?: string | null,
): OnboardingDraft | null {
  try {
    const stored = localStorage.getItem(ONBOARDING_DRAFT_KEY);
    if (!stored) return null;

    const draft: OnboardingDraft = JSON.parse(stored);

    // If we have a current user, only restore draft that belongs to them
    if (currentUserId != null) {
      if (draft.userId !== currentUserId) {
        logger.info("[ONBOARDING] Draft belongs to another user - clearing", {
          draftUserId: draft.userId ?? "(none)",
          currentUserId,
        });
        clearOnboardingDraft();
        return null;
      }
    }

    // Check if draft has expired
    const expiresAt = new Date(draft.expiresAt);
    if (expiresAt < new Date()) {
      logger.info("[ONBOARDING] Draft expired - clearing");
      clearOnboardingDraft();
      return null;
    }

    logger.info("[ONBOARDING] Draft loaded", {
      step: draft.currentStep,
      savedAt: draft.savedAt,
      userId: draft.userId ?? "(none)",
    });

    return draft;
  } catch (error) {
    logger.error("[ONBOARDING] Failed to load draft:", error);
    return null;
  }
}

/**
 * Clear onboarding draft from localStorage
 */
export function clearOnboardingDraft(): void {
  try {
    localStorage.removeItem(ONBOARDING_DRAFT_KEY);
    logger.debug("[ONBOARDING] Draft cleared");
  } catch (error) {
    logger.error("[ONBOARDING] Failed to clear draft:", error);
  }
}

/**
 * Check if a draft exists for the current user (pass userId to scope the check).
 */
export function hasDraft(currentUserId?: string | null): boolean {
  try {
    const draft = loadOnboardingDraft(currentUserId);
    return draft !== null;
  } catch {
    return false;
  }
}
